import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { getTrackingCodeStatusColor } from '../../utils/statusColors';

interface TrackingCode {
  trackingOptionID: string;
  name: string;
  status: string;
  categoryID: string;
  categoryName: string;
}

export const TrackingCodesList: React.FC = () => {
  const { t } = useTranslation();
  const [trackingCodes, setTrackingCodes] = useState<TrackingCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchTrackingCodes();
  }, []);

  const fetchTrackingCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      // Check if Xero is connected
      const settingsResponse = await fetch(`${apiUrl}/api/xero/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const settingsData = await settingsResponse.json();
      
      if (settingsData.success && settingsData.data) {
        setIsConnected(true);

        // Fetch all tracking codes
        const codesResponse = await fetch(`${apiUrl}/api/xero/tracking-categories`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const codesData = await codesResponse.json();
        
        if (codesData.success) {
          setTrackingCodes(codesData.data || []);
        } else {
          setError(codesData.message || t('admin.trackingCodesFetchError'));
        }
      } else {
        setIsConnected(false);
      }
    } catch (err) {
      console.error('Fetch tracking codes error:', err);
      setError(t('admin.trackingCodesFetchError'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600 dark:text-gray-400">
          {t('admin.xeroNotConnectedMessage')}
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('admin.trackingCodes')}
        </h3>
        <button
          onClick={fetchTrackingCodes}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title={t('common.refresh')}
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-error-400">{error}</p>
        </div>
      )}

      {trackingCodes.length === 0 ? (
        <div className="p-4 bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p className="text-sm text-warning-800 dark:text-warning-400">
            {t('admin.noTrackingCodesFound')}
          </p>
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
          <div className="divide-y divide-gray-200 dark:divide-gray-600">
            {trackingCodes.map((code) => (
              <div
                key={code.trackingOptionID}
                className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-between"
              >
                <div className="flex-1">
                  <span className="text-sm text-gray-900 dark:text-gray-100">
                    {code.name}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    ({code.categoryName})
                  </span>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${getTrackingCodeStatusColor(code.status)}`}>
                  {code.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

